import React from "react";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";

import { formatDateReadable } from "../../requests/utilities/helpers";


/**
 * Click-to-open date popover matching the existing employee-edit drawer style.
 * Click-outside detection closes the popover. Selection auto-closes.
 *
 * Props:
 *   value: ISO date string ("YYYY-MM-DD") or empty string
 *   onChange: (next: string) => void
 *   placeholder: optional, defaults to "Click to select"
 */
export default function DatePickerField({ value, onChange, placeholder = "Click to select" }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div
      ref={ref}
      className={`relative flex-1 rounded-md text-sm cursor-pointer bg-white ${
        open
          ? "border-2 border-violet-700"
          : "border border-[#bdbdbd] hover:border-slate-900"
      }`}
    >
      <div
        className="w-full h-full px-3 py-2"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-900">
            {value ? formatDateReadable(value) : placeholder}
          </p>
          <CalendarTodayIcon style={{ fontSize: 16 }} className="text-slate-500" />
        </div>
      </div>
      {open && (
        // z-[1600] puts the popover above the OperationsAssistantPanel's
        // chrome (panel container itself sits at zIndex 1400 with
        // internal menus at 1500). Without this, opening the calendar
        // from inside a proposal card hides it behind the panel header
        // / input bar. The default Tailwind z-30 was fine outside the
        // panel context but loses there.
        <div className="absolute top-full left-0 z-[1600] mt-1 bg-white flex items-center justify-center pl-4 pr-5 border border-[#bdbdbd] rounded-lg shadow-xl overflow-hidden">
          <div className="mt-3">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DateCalendar"]}>
                <DemoItem>
                  <DateCalendar
                    value={value ? dayjs(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue ? newValue.format("YYYY-MM-DD") : "");
                      setOpen(false);
                    }}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                  />
                </DemoItem>
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
      )}
    </div>
  );
}
